import React, { useState, useCallback, useEffect } from 'react';
import {
  CheckCircle2, Eye, Calendar, X, FileWarning,
  FileText, CloudUpload, AlertTriangle,
  Loader2, Clock, ShieldCheck, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/api/api';
import { toast } from 'sonner';

export default function DocumentManager({ entityId, entityType, requirements, uploads, loading, onRefresh }) {
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadData, setUploadData] = useState({ file: null, expiry_date: '' });
  const [isViewing, setIsViewing] = useState(null);

  useEffect(() => {
    if (selectedType) {
      setUploadData({ file: null, expiry_date: '' });
      setIsDragging(false);
    }
  }, [selectedType?.id]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setUploadData((prev) => ({ ...prev, file }));
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setUploadData((prev) => ({ ...prev, file }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !selectedType) return;

    const typeId = selectedType.id;
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('type_id', typeId);
    formData.append('entity_id', Number(entityId));
    formData.append('entity_type', entityType);
    formData.append('expiry_date', uploadData.expiry_date || '');

    try {
      setUploadProgress((prev) => ({ ...prev, [typeId]: 0 }));

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => {
          const percent = Math.round((p.loaded * 100) / p.total);
          setUploadProgress((prev) => ({ ...prev, [typeId]: percent }));
        }
      });

      toast.success('Documento carregado com sucesso');
      setSelectedType(null);
      setTimeout(() => {
        setUploadProgress((prev) => { const n = { ...prev }; delete n[typeId]; return n; });
        onRefresh();
      }, 1000);
    } catch (error) {
      toast.error('Falha ao carregar documento');
      setUploadProgress((prev) => { const n = { ...prev }; delete n[typeId]; return n; });
    }
  };

  const getDocForType = (typeId) => uploads.find((d) => d.type_id === typeId);

  const getStatusDetails = (doc) => {
    if (!doc) return { color: 'text-red-600', bg: 'bg-red-50', label: 'Em Falta', icon: AlertTriangle };
    const diff = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { color: 'text-red-600', bg: 'bg-red-100', label: 'Expirado', icon: FileWarning };
    if (diff <= 15) return { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Expira Breve', icon: Clock };
    return { color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Conforme', icon: CheckCircle2 };
  };

  const viewDocument = async (doc) => {
    setIsViewing(doc.id);
    try {
      const response = await api.get(`/documents/view/${doc.id}`, { responseType: 'blob' });
      const fileURL = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      window.open(fileURL, '_blank');
      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    } catch (err) {
      toast.error('Acesso negado');
    } finally {
      setIsViewing(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Protocolo Documental</th>
              <th className="px-4 py-3 text-center font-medium text-muted-foreground uppercase">Audit</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Validade</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground uppercase">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : (
              requirements.map((type) => {
                const doc = getDocForType(type.id);
                const status = getStatusDetails(doc);
                const isUploading = uploadProgress[type.id] !== undefined;

                return (
                  <tr key={type.id} className="hover:bg-muted/30">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${doc ? 'bg-slate-900 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <FileText size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{type.name}</p>
                          <p className="text-xs text-muted-foreground uppercase">ID: {entityType.toUpperCase()}-{type.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {doc ? (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => viewDocument(doc)}
                          disabled={isViewing === doc.id}
                        >
                          {isViewing === doc.id ? <Loader2 className="animate-spin" size={18} /> : <Eye size={18} />}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {doc ? (
                        <span className="text-sm font-semibold">{new Date(doc.expiry_date).toLocaleDateString()}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground uppercase">Aguardando</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {isUploading ? (
                        <div className="flex flex-col gap-1 w-28">
                          <div className="flex justify-between text-xs text-primary font-semibold">
                            <span>Sincronizando...</span>
                            <span>{uploadProgress[type.id]}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress[type.id]}%` }} />
                          </div>
                        </div>
                      ) : (
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase border ${status.bg} ${status.color}`}>
                          <status.icon size={12} />
                          {status.label}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button onClick={() => setSelectedType(type)}>
                        {doc ? 'Substituir' : 'Carregar'}
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <Card className="w-full max-w-xl relative">
            <CardContent className="p-6 space-y-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-xl flex items-center justify-center">
                    <CloudUpload size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Certificação</h3>
                    <p className="text-xs text-muted-foreground uppercase">Upload para: {selectedType.name}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedType(null)}>
                  <X size={20} />
                </Button>
              </div>

              <form onSubmit={handleUpload} className="space-y-6">
                <div
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-2xl p-10 transition-all flex flex-col items-center justify-center gap-4 cursor-pointer ${
                    isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30 bg-muted/30'
                  } ${uploadData.file ? 'border-emerald-200 bg-emerald-50' : ''}`}
                >
                  <input type="file" onChange={handleFileSelect} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />

                  {uploadData.file ? (
                    <>
                      <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center">
                        <CheckCircle2 size={32} />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-bold text-emerald-600 uppercase">{uploadData.file.name}</p>
                        <p className="text-xs text-muted-foreground">Ficheiro pronto para submissão</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-background text-muted-foreground rounded-2xl flex items-center justify-center">
                        <CloudUpload size={32} />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold">Arraste o ficheiro ou clique para selecionar</p>
                        <p className="text-xs text-muted-foreground">PDF, PNG ou JPG até 5MB</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-2">
                      <Calendar size={14} className="text-primary" /> Data de Validade
                    </label>
                    <Input
                      type="date"
                      required
                      value={uploadData.expiry_date}
                      onChange={(e) => setUploadData((prev) => ({ ...prev, expiry_date: e.target.value }))}
                    />
                  </div>
                  <div className="hidden md:block p-4 bg-primary/5 rounded-xl border border-primary/10">
                    <div className="flex items-center gap-3 text-primary">
                      <ShieldCheck size={20} />
                      <p className="text-xs font-bold uppercase">Upload seguro e auditado</p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={!uploadData.file || !uploadData.expiry_date}
                  className="w-full"
                >
                  <Zap size={18} className="mr-2" />
                  Sincronizar Arquivo
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-32" /></td>
      <td className="px-4 py-4 text-center"><div className="h-9 w-9 bg-muted rounded-lg mx-auto" /></td>
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-20" /></td>
      <td className="px-4 py-4"><div className="h-6 bg-muted rounded-full w-24" /></td>
      <td className="px-4 py-4 text-right"><div className="h-9 bg-muted rounded w-24 ml-auto" /></td>
    </tr>
  );
}
